import React, { createContext, useContext, useEffect, useState } from "react";
import { fetchAuthSession, getCurrentUser, signOut } from "aws-amplify/auth";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const REMEMBER_ME_KEY = "trivistaRememberMe";

  const clearRememberMeIfExpired = async () => {
    if (typeof window === "undefined") return;

    try {
      const raw = window.localStorage.getItem(REMEMBER_ME_KEY);
      if (!raw) return;

      const data = JSON.parse(raw);
      if (!data?.enabled || !data?.expiresAt) return;

      const now = Date.now();
      if (now > data.expiresAt) {
        await signOut();
        window.localStorage.removeItem(REMEMBER_ME_KEY);
        setUser(null);
      }
    } catch (error) {
      console.error("Error handling remember me expiry:", error);
    }
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      await clearRememberMeIfExpired();

      const currentUser = await getCurrentUser();
      await fetchAuthSession();
      setUser(currentUser);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const value = {
    user,
    loading,
    logout,
    checkAuthStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
